import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { Layer, Rectangle, ResponsiveContainer, Sankey, Tooltip } from 'recharts'
import { CHART_TOOLTIP_PROPS } from '@/config/chartTheme'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import {
  NOT_RECORDED_COLOR,
  OUTCOME_COLOR,
  compactCurrency,
  goldCategory,
  drillForIds,
  type Outcome,
  type PersonProfile,
  type SankeyNode,
} from '@/services/dashboardAnalytics'

interface ChartNode {
  name: string
  id: string
  kind: SankeyNode['kind']
  color: string
  ids: string[]
}

interface ChartLink {
  source: number
  target: number
  value: number
  count: number
  amount: number | null
  dealIds: string[]
}

const OUTCOME_BY_LABEL: Record<string, Outcome> = {
  Requested: 'requested',
  Signed: 'signed',
  Funded: 'funded',
  Dead: 'dead',
}

/** Pure: the server's node/link lists as recharts' index-addressed graph,
 *  valued by deal count or by requested volume. Zero-valued links are
 *  dropped (recharts cannot lay out a zero-width band). Tested. */
export function buildSankey(
  sankey: PersonProfile['sankey'] | null | undefined,
  metric: 'count' | 'amount',
): { nodes: ChartNode[]; links: ChartLink[] } {
  if (!sankey) return { nodes: [], links: [] }
  const value = (link: { count: number; amount: number | null }) => (metric === 'count' ? link.count : link.amount ?? 0)
  const kept = sankey.links.filter((link) => value(link) > 0)
  const used = new Set(kept.flatMap((link) => [link.source, link.target]))
  const sourceOrder = new Map<string, number>()
  const nodes: ChartNode[] = []
  const index = new Map<string, number>()
  for (const node of sankey.nodes) {
    if (!used.has(node.id)) continue
    let color = '#C49A43'
    if (node.kind === 'outcome') {
      const outcome = OUTCOME_BY_LABEL[node.label] ?? (node.id.replace('outcome:', '') as Outcome)
      color = OUTCOME_COLOR[outcome] ?? NOT_RECORDED_COLOR
    } else if (node.kind === 'source') {
      const n = sourceOrder.size
      sourceOrder.set(node.id, n)
      color = node.label === 'Not recorded' ? NOT_RECORDED_COLOR : goldCategory(n + 2)
    }
    index.set(node.id, nodes.length)
    nodes.push({ name: node.label, id: node.id, kind: node.kind, color, ids: [] })
  }
  const links: ChartLink[] = kept.map((link) => ({
    source: index.get(link.source)!,
    target: index.get(link.target)!,
    value: value(link),
    count: link.count,
    amount: link.amount,
    dealIds: link.dealIds,
  }))
  // A node's deals: what flows out of a source, into an outcome, through the person.
  for (const link of links) {
    const from = nodes[link.source]
    const to = nodes[link.target]
    if (from.kind === 'source') from.ids.push(...link.dealIds)
    if (to.kind === 'outcome') to.ids.push(...link.dealIds)
    if (from.kind === 'person' && to.kind === 'outcome') from.ids.push(...link.dealIds)
  }
  return { nodes, links }
}

interface NodeProps {
  x: number
  y: number
  width: number
  height: number
  index: number
  payload: ChartNode & { value: number }
}

/** Sources and the person label to their right; outcomes (the last column)
 *  label to their left so no name runs off the plot. */
function NodeShape({ x, y, width, height, payload }: NodeProps) {
  const right = payload.kind === 'outcome'
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={Math.max(height, 1)} fill={payload.color} fillOpacity={0.95} radius={1} />
      {height > 7 && (
        <text
          x={right ? x - 6 : x + width + 6}
          y={y + height / 2 + 3.5}
          textAnchor={right ? 'end' : 'start'}
          className="fill-stone-300 text-[10px]"
          paintOrder="stroke"
          stroke="rgba(8,8,8,0.85)"
          strokeWidth={3}
        >
          {payload.name.length > 26 ? `${payload.name.slice(0, 25)}…` : payload.name}
        </text>
      )}
    </Layer>
  )
}

interface LinkProps {
  sourceX: number
  targetX: number
  sourceY: number
  targetY: number
  sourceControlX: number
  targetControlX: number
  linkWidth: number
  payload: ChartLink & { source: ChartNode; target: ChartNode }
}

function LinkShape({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload }: LinkProps) {
  const [hover, setHover] = useState(false)
  const color = payload.target.kind === 'outcome' ? payload.target.color : payload.source.color
  return (
    <path
      d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
      fill="none"
      stroke={color}
      strokeOpacity={hover ? 0.6 : 0.28}
      strokeWidth={Math.max(1, linkWidth)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ cursor: 'pointer', transition: 'stroke-opacity 120ms' }}
    />
  )
}

interface RelationshipSankeyProps {
  sankey: PersonProfile['sankey'] | null | undefined
  personName: string
  className?: string
}

/**
 * Where the person's deals come from and where they end up: brokerage →
 * person → outcome (for a brokerage, its brokers → brokerage → outcome). A
 * band's width is its deal count (or volume); clicking a band or a node opens
 * exactly the deals in it.
 */
export default function RelationshipSankey({ sankey, personName, className }: RelationshipSankeyProps) {
  const { open } = useDrillDown()
  const [metric, setMetric] = useState<'count' | 'amount'>('count')
  const graph = useMemo(() => buildSankey(sankey, metric), [sankey, metric])

  if (graph.links.length === 0) {
    return <p className="px-1 py-6 text-center text-[11px] text-stone-500">No deal flow to draw for {personName}.</p>
  }

  const onClick = (element: { payload?: unknown }, type: string) => {
    if (type === 'link') {
      const link = element.payload as ChartLink & { source: ChartNode; target: ChartNode }
      open(drillForIds(link.dealIds, `${link.source.name} → ${link.target.name}`))
    } else if (type === 'node') {
      const node = element.payload as ChartNode
      open(drillForIds([...new Set(node.ids)], node.kind === 'person' ? `${personName} — all deals` : `${personName} — ${node.name}`))
    }
  }

  return (
    <div className={clsx('flex min-h-0 flex-1 flex-col gap-1', className)}>
      <div className="flex flex-shrink-0 items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[9.5px] text-stone-500">
          {(['funded', 'signed', 'requested', 'dead'] as Outcome[]).map((outcome) => (
            <span key={outcome} className="flex items-center gap-1">
              <span className="h-1.5 w-2.5 rounded-sm" style={{ background: OUTCOME_COLOR[outcome] }} aria-hidden="true" />
              {outcome[0].toUpperCase() + outcome.slice(1)}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {(['count', 'amount'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setMetric(key)}
              aria-pressed={metric === key}
              className={clsx(
                'rounded-full border px-2 py-0.5 text-[9.5px] transition-colors',
                metric === key ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200' : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
              )}
            >
              {key === 'count' ? 'Deals' : 'Volume'}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-[220px] flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <Sankey
            data={{ nodes: graph.nodes, links: graph.links }}
            nodeWidth={9}
            nodePadding={9}
            linkCurvature={0.55}
            iterations={48}
            margin={{ top: 6, right: 8, bottom: 6, left: 8 }}
            node={(props: NodeProps) => <NodeShape {...props} />}
            link={(props: LinkProps) => <LinkShape {...props} />}
            onClick={onClick}
          >
            <Tooltip
              {...CHART_TOOLTIP_PROPS}
              formatter={(value: number, _name: string, item: { payload?: { payload?: ChartLink } }) => {
                const link = item?.payload?.payload
                if (link && typeof link.count === 'number') {
                  return [`${link.count} deal${link.count === 1 ? '' : 's'} · ${compactCurrency(link.amount)}`, '']
                }
                return [metric === 'count' ? `${value} deals` : compactCurrency(value), '']
              }}
            />
          </Sankey>
        </ResponsiveContainer>
      </div>
      {sankey?.basis && <p className="flex-shrink-0 text-[9.5px] leading-snug text-stone-500">{sankey.basis}</p>}
    </div>
  )
}
